import { useLazyQuery, useMutation } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import GoalTemplateTableFilters from "@components/forms/GoalTemplateTableFilters";
import HandleQuery from "@components/HandleQuery";
import Table, { TableHeader } from "@components/Table";
import TagList from "@components/TagList";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToAllGoalTemplates from "@hooks/subscriptions/useSubscribeToAllGoalTemplates";
import useMyAdmin from "@hooks/useMyAdmin";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  ExpertGlobalRoleEnum,
  GetGoalTemplatesDocument,
  GetGoalTemplatesQuery,
  GetGoalTemplatesQueryVariables,
  GoalTemplateKindEnum,
  GoalTemplateSortByEnum,
  GoalTemplateUpdateDocument,
  GoalTemplateUpdateMutation,
  GoalTemplateUpdateMutationVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Filters = ComponentProps<typeof GoalTemplateTableFilters>["filters"];

type Props = {
  journeyId?: string;
};

type GoalTemplate = NonNullable<
  NonNullable<NonNullable<GetGoalTemplatesQuery["goalTemplates"]["edges"]>[number]>["node"]
>;

const GoalTemplatesTable = ({ journeyId }: Props) => {
  const goalTemplateArchiveOpener = useOpener();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const myAdmin = useMyAdmin();
  const myExpertGlobalRole = myExpert?.expertGlobalRole;

  const [editingGoalTemplate, setEditingGoalTemplate] = useState<GoalTemplate>();
  const [sortBy, setSortBy] = useState<GoalTemplateSortByEnum>(GoalTemplateSortByEnum.TITLE);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.ASCENDING);

  const [getGoalTemplates, getGoalTemplatesQuery] = useLazyQuery<GetGoalTemplatesQuery, GetGoalTemplatesQueryVariables>(
    GetGoalTemplatesDocument
  );

  const [updateGoalTemplate, { client }] = useMutation<GoalTemplateUpdateMutation, GoalTemplateUpdateMutationVariables>(
    GoalTemplateUpdateDocument,
    {
      update: () => {
        // This kicks out all of the goal templates queries so we will re-make them. If you're flipping between filters
        // archiving will update the goal, but switching filters will have the cached value
        client.cache.evict({ fieldName: "goalTemplates", id: "ROOT_QUERY" });
        getGoalTemplatesQuery.refetch();
      }
    }
  );

  const goalTemplates = useNodesFromConnection(getGoalTemplatesQuery.data?.goalTemplates);

  const [filters, setFilters] = useState<Filters>({
    archived: undefined,
    focusArea: undefined,
    kind: GoalTemplateKindEnum.ALL,
    searchTerm: "",
    usedInJourney: false
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const handleGoalTemplateArchive = useCallback(
    (goalTemplate: GoalTemplate) => () => {
      setEditingGoalTemplate(goalTemplate);
      goalTemplateArchiveOpener.open();
    },
    [goalTemplateArchiveOpener]
  );

  const handleGoalTemplateArchived = useCallback(() => {
    if (editingGoalTemplate) {
      updateGoalTemplate({
        variables: {
          archived: !editingGoalTemplate.archived,
          id: editingGoalTemplate.id
        }
      });
    }
  }, [editingGoalTemplate, updateGoalTemplate]);

  useEffect(() => {
    getGoalTemplates({
      variables: {
        archived: filters.archived,
        focusArea: filters.focusArea,
        journeyId,
        kind: filters.kind,
        searchTerm: filters.searchTerm,
        sortBy,
        sortDirection,
        usedInJourneyId: (filters.usedInJourney && journeyId) || null
      }
    });
  }, [
    filters.archived,
    filters.focusArea,
    filters.kind,
    filters.searchTerm,
    filters.usedInJourney,
    getGoalTemplates,
    journeyId,
    sortBy,
    sortDirection
  ]);

  useSubscribeToAllGoalTemplates(() => {
    getGoalTemplatesQuery.refetch();
  });

  const tableHeaders: TableHeader<GoalTemplateSortByEnum>[] = [
    { columnSortBy: GoalTemplateSortByEnum.TITLE },
    { messageKey: "models.goalTemplate.tableHeadings.tags" },
    { messageKey: "models.goalTemplate.tableHeadings.focusAreas" },
    { messageKey: "models.goalTemplate.tableHeadings.journey" },
    { columnSortBy: GoalTemplateSortByEnum.CREATED_AT },
    { columnSortBy: GoalTemplateSortByEnum.UPDATED_AT },
    { messageKey: "models.goalTemplate.tableHeadings.author" },
    { messageKey: "models.goalTemplate.tableHeadings.actions" }
  ];

  return (
    <>
      {!myAdmin && (
        <div className="ion-margin-top">
          <Button
            routerLink={
              journeyId
                ? journeyRoute({ journeyId, linkAfterJourney: "/goal-templates/new", myExpert })
                : `/expert/goal-templates/new`
            }
          >
            <FormattedMessage id="pages.expert.goalTemplates.addGoal" />
          </Button>
        </div>
      )}
      <GoalTemplateTableFilters filters={filters} journeyId={journeyId} onChange={handleFilterChange} />
      <HandleQuery query={getGoalTemplatesQuery}>
        {data => (
          <Table<GoalTemplateSortByEnum>
            fetchMore={getGoalTemplatesQuery.fetchMore}
            headerKey="models.goalTemplate.tableHeadings"
            headers={tableHeaders}
            pageInfo={data.goalTemplates.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {goalTemplates.map(goalTemplate => (
              <tr key={goalTemplate.id}>
                <td>
                  {myAdmin || myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER || goalTemplate.journey?.id ? (
                    <InternalLink
                      to={
                        myAdmin
                          ? `/admin/goal-templates/${goalTemplate.id}`
                          : journeyId
                            ? journeyRoute({
                                journeyId,
                                linkAfterJourney: `/goal-templates/${goalTemplate.id}/preview`,
                                myExpert
                              })
                            : `/expert/goal-templates/${goalTemplate.id}`
                      }
                    >
                      {goalTemplate.title}
                    </InternalLink>
                  ) : (
                    <>{goalTemplate.title}</>
                  )}
                </td>
                <td>
                  <TagList tags={goalTemplate.tags} />
                </td>
                <td>
                  {goalTemplate.focusAreas
                    .map(focusArea => intl.formatMessage({ id: `models.focusArea.${focusArea}` }))
                    .join(", ")}
                </td>
                <td>
                  {goalTemplate.journey && (
                    <InternalLink
                      to={journeyRoute({ journeyId: goalTemplate.journey.id, linkAfterJourney: "", myExpert })}
                    >
                      <FormattedMessage
                        id="models.journey.nameWithOwner"
                        values={{
                          lovedOne: getCareProfileName({ intl, journey: goalTemplate.journey }),
                          owner: goalTemplate.journey.name
                        }}
                      />
                    </InternalLink>
                  )}
                </td>
                <td>
                  <DateStamp showWeekday={false} value={goalTemplate.createdAt} />
                </td>
                <td>
                  <DateStamp showWeekday={false} value={goalTemplate.updatedAt} />
                </td>
                <td>{goalTemplate.author.fullName}</td>
                <td>
                  {(myAdmin || myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER || goalTemplate.journey?.id) && (
                    <>
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.edit"
                        icon="edit"
                        routerLink={
                          journeyId
                            ? journeyRoute({
                                journeyId,
                                linkAfterJourney: `/goal-templates/${goalTemplate.id}/edit`,
                                myExpert
                              })
                            : myExpert
                              ? `/expert/goal-templates/${goalTemplate.id}/edit`
                              : `/admin/goal-templates/${goalTemplate.id}/edit`
                        }
                      />

                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.archive"
                        icon="boxArchive"
                        onClick={handleGoalTemplateArchive(goalTemplate)}
                      />
                    </>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>

      <DeleteConfirmationAlert
        deleteKey={editingGoalTemplate?.archived ? "dictionary.unarchive" : "dictionary.archive"}
        messageKey={`pages.expert.goalTemplates.${editingGoalTemplate?.archived ? "unarchive" : "archive"}`}
        messageValue={editingGoalTemplate?.title}
        messageValueKey="title"
        onDelete={handleGoalTemplateArchived}
        opener={goalTemplateArchiveOpener}
      />
    </>
  );
};

export default GoalTemplatesTable;
